import React, { ReactElement } from 'react'
import { Link } from 'react-router'

import paths from '../../constants/paths'
import Company from '../../model/company'
import Button from '../elements/button'
import Headline from '../elements/Headline'
import Subtitle from '../elements/Subtitle'
import TitleMenu from '../elements/TitleMenu'

import './Terms.css'

type Props = {
  company?: Company
}

export default function CookiePolicy(props: Props): ReactElement | null {
  const handleRenewConsent = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ci = (window as any).CookieInformation
    if (ci && typeof ci.renew === 'function') {
      ci.renew()
    }
  }

  return (
    <div className="terms full-height">
      <div className="terms-text">
        <TitleMenu>
          {props.company && (
            <Link to={'/' + paths.COMPANIES + '/' + props.company.id + '/support'}>
              <Button>Tilbage</Button>
            </Link>
          )}
          <Button onClick={() => window.print()}>Download PDF</Button>
        </TitleMenu>

        <Subtitle>Cookiepolitik</Subtitle>
        <p>
          <small>Den nuværende version af dette dokument er gældende fra [2026-04-13].</small>
        </p>

        <Headline>Generelt</Headline>
        <p>
          Denne cookiepolitik forklarer, hvordan Salary ("vi", "os" eller "vores") bruger cookies og lignende
          sporingsteknologier ("Cookies") på vores hjemmeside for at sikre funktionalitet, forbedre brugeroplevelsen,
          analysere trafik og levere personlig markedsføring.
        </p>
        <p>
          Ved dit første besøg vil et cookiebanner bede om dit samtykke til ikke-essentielle cookies. Du kan til enhver
          tid ændre eller trække dit samtykke tilbage via vores{' '}
          <Button className="terms-inline-link" onClick={handleRenewConsent}>
            Panel til Cookie-indstillinger
          </Button>
          .
        </p>

        <Headline>Hvad er cookies?</Headline>
        <p>
          Cookies er små tekstfiler, der placeres på din enhed, når du besøger en hjemmeside. De bruges til at indsamle
          standardoplysninger om internetlogning og besøgsadfærd. Disse oplysninger hjælper os med at forstå, hvordan du
          bruger vores side, og giver os mulighed for at tilbyde en bedre oplevelse. Hver gang du vender tilbage til
          hjemmesiden eller den relevante applikation, hentes cookien fra din browser eller enhed. Dette gør det muligt
          at genkende din browser eller enhed ved efterfølgende besøg.
        </p>
        <p>
          Endeligt kan placeringen af disse cookies give os adgang til dine browserdata og/eller personoplysninger om
          dig.
        </p>
        <p>Cookies kan være:</p>
        <ul>
          <li>
            <strong>Sessionscookies</strong> – slettes, når du lukker din browser.
          </li>
          <li>
            <strong>Persistente cookies</strong> – bliver liggende på din enhed i en fastsat periode, eller indtil de
            slettes manuelt.
          </li>
        </ul>

        <Headline>Typer af cookies vi bruger</Headline>

        <p>
          <strong>Strengt nødvendige</strong>
          <br />
          Disse cookies er essentielle for vores hjemmesides og tjenesters korrekte funktion, sikkerhed og tekniske
          drift.
          <br />
          <em>Retsgrundlag: Berettiget interesse (Art. 6, stk. 1, litra f, i GDPR)</em>
        </p>

        <p>
          <strong>Præferencecookies (Funktionelle cookies)</strong>
          <br />
          Disse cookies hjælper med at huske dine præferencer og indstillinger (f.eks. sprog- eller layoutvalg) for at
          give en personlig og brugervenlig oplevelse.
          <br />
          <em>Retsgrundlag: Samtykke (Art. 6, stk. 1, litra a, i GDPR)</em>
        </p>

        <p>
          <strong>Analyse- og målingscookies</strong>
          <br />
          Disse cookies hjælper os med at måle webtrafik (antal besøg, sidevisninger, brugeraktivitet). De kan indsamle
          anonymiserede data, såsom trunkerede IP-adresser, for at bestemme brugergeolokation (f.eks. byniveau). De
          bruges også til at opdage navigationsproblemer, generere brugsstatistikker og forbedre webstedets ydeevne.
          <br />
          <em>Retsgrundlag: Samtykke (Art. 6, stk. 1, litra a, i GDPR)</em>
        </p>

        <p>
          <strong>Annoncerings- og Retargetingcookies</strong>
          <br />
          Disse cookies bruges til at levere personlige annoncer og måle effektiviteten af annoncekampagner. De kan
          indstilles af vores hjemmeside eller tredjeparter, der viser annoncer, widgets eller interaktive elementer. Vi
          kan også bruge avancerede målretningsfunktioner såsom retargeting (f.eks. annoncer vist på tredjeparters
          websteder baseret på tidligere besøg eller produktvisninger på vores side). Disse cookies hjælper med at
          opbygge remarketingmålgrupper.
          <br />
          Annonceplatforme, der bruges: Facebook, Google Ads, Google Display &amp; Video 360, Microsoft Advertising,
          LinkedIn Ads, Snapchat, TikTok, Amazon Ads, Zemanta.
          <br />
          <em>Retsgrundlag: Samtykke (Art. 6, stk. 1, litra a, i GDPR)</em>
        </p>

        <p>
          <strong>Indholdspersonaliseringcookies</strong>
          <br />
          Disse cookies giver os mulighed for at skræddersy det indhold, der vises på vores side, til dine interesser,
          baseret på din browseradfærd og profil.
          <br />
          <em>Retsgrundlag: Samtykke (Art. 6, stk. 1, litra a, i GDPR)</em>
        </p>

        <p>
          <strong>Cookies til sociale medier</strong>
          <br />
          Disse cookies gør det muligt for brugere at dele indhold fra vores platform på sociale medienetværk (f.eks.
          via "synes godt om" eller "del"-knapper). De kan også spore brugeraktivitet på vores hjemmeside. Vi opfordrer
          dig til at læse de relevante sociale netværks fortrolighedspolitikker for at forstå, hvordan de bruger dine
          browserdata, og hvordan du udøver dine rettigheder.
          <br />
          <em>Retsgrundlag: Samtykke (Art. 6, stk. 1, litra a, i GDPR)</em>
        </p>

        <p>
          <strong>Genkendelse på tværs af enheder</strong>
          <br />
          Nogle cookies og identifikatorer kan bruges til at knytte dine besøg sammen på tværs af forskellige enheder
          eller miljøer (f.eks. computer, smartphone) for at sikre en sammenhængende og personlig oplevelse på tværs af
          sessioner.
          <br />
          <em>Retsgrundlag: Samtykke (Art. 6, stk. 1, litra a, i GDPR)</em>
        </p>

        <Headline>Administration af dine cookie-indstillinger</Headline>
        <p>
          <strong>Samtykke:</strong> Når du besøger vores hjemmeside første gang, bliver du opfordret til at acceptere
          brugen af cookies via knappen "Acceptér", der vises på cookie-banneret. Ved at acceptere giver du samtykke til
          placering af cookies på din enhed til de beskrevne formål.
        </p>
        <p>
          <strong>Afvisning:</strong> Du kan afvise ikke-essentielle cookies ved at klikke på "Fortsæt uden at
          acceptere" eller ved at justere dine præferencer i panelet for cookie-indstillinger, som er tilgængeligt via
          knappen "Indstillinger". Bemærk venligst, at hvis du afviser cookies, kan der stadig vises
          ikke-personliggjorte annoncer på de besøgte hjemmesider.
        </p>
        <p>
          <strong>Ændring af dine præferencer:</strong> Du kan til enhver tid trække dit samtykke tilbage eller ændre
          dine cookie-indstillinger ved at tilgå{' '}
          <button className="terms-inline-link" onClick={handleRenewConsent}>
            Panelet for Cookie-indstillinger
          </button>
          , som findes nederst på hver side på vores websted.
        </p>
        <p>Sådan sletter du cookies manuelt:</p>
        <ul>
          <li>
            <strong>Windows:</strong> Tryk på CTRL + SHIFT + DELETE i din browser.
          </li>
          <li>
            <strong>Mac:</strong> Tryk på CMD + SHIFT + DELETE.
          </li>
        </ul>
        <p>Deaktivering af visse cookies kan påvirke hjemmesidens funktionalitet eller ydeevne.</p>

        <Headline>De cookies, vi bruger</Headline>
        <p>
          Vi bruger både vores egne cookies og cookies fra betroede tredjepartsudbydere. En komplet og opdateret liste
          over de anvendte cookies – herunder navn, udbyder, formål, varighed og funktion – kan findes i denne oversigt:
        </p>

        <div className="terms-table-wrapper">
          <table className="terms-table">
            <thead>
              <tr>
                <th>Navn</th>
                <th>Udbyder</th>
                <th>Service</th>
                <th>Formål</th>
                <th>Udløb</th>
                <th>Funktion</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>tracking</td>
                <td>salary.dk</td>
                <td></td>
                <td>Uklassificerede</td>
                <td>Permanent</td>
                <td>Uklassificeret</td>
              </tr>
              <tr>
                <td>bcookie</td>
                <td>linkedin.com</td>
                <td>LinkedIn</td>
                <td>
                  Supports online marketing by collecting information encouraging to promote products through partners
                  and other platforms.
                </td>
                <td>1 year</td>
                <td>Marketing</td>
              </tr>
              <tr>
                <td>CookieInformationConfig</td>
                <td>policy.app.cookieinformation.com</td>
                <td>Cookie Information</td>
                <td>Supports the website's technical functions.</td>
                <td>1 year</td>
                <td>Necessary</td>
              </tr>
              <tr>
                <td>CookieInformationConsent</td>
                <td>app.salary.dk</td>
                <td>Cookie Information</td>
                <td>Supports the website's technical functions.</td>
                <td>1 year</td>
                <td>Necessary</td>
              </tr>
              <tr>
                <td>CookieInformationConsent</td>
                <td>salary.dk</td>
                <td>Cookie Information</td>
                <td>Supports the website's technical functions.</td>
                <td>1 year</td>
                <td>Necessary</td>
              </tr>
              <tr>
                <td>CookieinformationConsent_xxx</td>
                <td>policy.app.cookieinformation.com</td>
                <td>Cookie Information</td>
                <td>Used to share consent across domains.</td>
                <td>1 year</td>
                <td>Necessary</td>
              </tr>
              <tr>
                <td>_fbp</td>
                <td>.salary.dk</td>
                <td>Facebook</td>
                <td>Identifies browsers for the purpose of delivering advertisements and website statistics.</td>
                <td>3 months</td>
                <td>Marketing</td>
              </tr>
              <tr>
                <td>_ga</td>
                <td>.salary.dk</td>
                <td>Google Analytics</td>
                <td>
                  Collects information about users and their activity on the website for analysis and reporting
                  purposes.
                </td>
                <td>1 year</td>
                <td>Statistic</td>
              </tr>
              <tr>
                <td>_ga_xxx</td>
                <td>.salary.dk</td>
                <td>Google Analytics</td>
                <td>
                  Persists the state of the session. It is used to distinguish users and maintain session continuity
                  within the analytics property identified by the container ID.
                </td>
                <td>1 year</td>
                <td>Statistic</td>
              </tr>
              <tr>
                <td>_gcl_au</td>
                <td>.salary.dk</td>
                <td>Google</td>
                <td>
                  Supports online marketing by collecting information encouraging users to promote products through
                  partners and other platforms.
                </td>
                <td>3 months</td>
                <td>Marketing</td>
              </tr>
              <tr>
                <td>IDE</td>
                <td>.doubleclick.net</td>
                <td>Google Marketing Platform</td>
                <td>
                  Used for online marketing by collecting information about users and their activity on the website. The
                  information is used to tailor advertising to the user across different channels and devices.
                </td>
                <td>1 year</td>
                <td>Marketing</td>
              </tr>
              <tr>
                <td>lidc</td>
                <td>.linkedin.com</td>
                <td>LinkedIn</td>
                <td>
                  Supports online marketing by collecting information encouraging users to promote products through
                  partners and other platforms.
                </td>
                <td>1 day</td>
                <td>Marketing</td>
              </tr>
              <tr>
                <td>li_gc</td>
                <td>.linkedin.com</td>
                <td>LinkedIn</td>
                <td>
                  Supports online marketing by collecting information encouraging users to promote products through
                  partners and other platforms.
                </td>
                <td>6 months</td>
                <td>Marketing</td>
              </tr>
              <tr>
                <td>m</td>
                <td>m.stripe.com</td>
                <td>Stripe</td>
                <td>Supports the website's technical functions.</td>
                <td>1 year</td>
                <td>Necessary</td>
              </tr>
              <tr>
                <td>_Secure-ROLLOUT_TOKEN</td>
                <td>.youtube.com</td>
                <td>Youtube, Google</td>
                <td>Supports the integration of a third-party platform on the website.</td>
                <td>6 months</td>
                <td>Marketing</td>
              </tr>
              <tr>
                <td>__Secure-YNID</td>
                <td>.youtube.com</td>
                <td>Youtube, Google</td>
                <td>
                  Collects information about the users and their activity on the website. The information is used to
                  track and analyse user behavior, to meet the individual user needs and to deliver targeted
                  advertising.
                </td>
                <td>6 months</td>
                <td>Marketing</td>
              </tr>
              <tr>
                <td>__stripe_mid</td>
                <td>.app.salary.dk</td>
                <td>Stripe</td>
                <td>Supports the website's technical functions.</td>
                <td>1 year</td>
                <td>Necessary</td>
              </tr>
              <tr>
                <td>__stripe_sid</td>
                <td>.app.salary.dk</td>
                <td>Stripe</td>
                <td>Supports the website's technical functions.</td>
                <td>30 minutes</td>
                <td>Necessary</td>
              </tr>
              <tr>
                <td>test_cookie</td>
                <td>.doubleclick.net</td>
                <td>Google Marketing Platform</td>
                <td>
                  Used for online marketing collecting information about users and their activity on the website. The
                  information is used to tailor advertising to the user across different channels and devices.
                </td>
                <td>15 minutes</td>
                <td>Marketing</td>
              </tr>
              <tr>
                <td>VISITOR_INFO1_LIVE</td>
                <td>.youtube.com</td>
                <td>Youtube, Google</td>
                <td>
                  Collects information about users and their activity on the website via embedded video players with the
                  aim of deliver targeted advertising.
                </td>
                <td>6 months</td>
                <td>Marketing</td>
              </tr>
              <tr>
                <td>VISITOR_PRIVACY_METADATA</td>
                <td>.youtube.com</td>
                <td>Youtube, Google</td>
                <td>
                  Collects information about users and their activity on the website. The information is used to track
                  and analyse user behaviour, in order to meet the needs of individual users.
                </td>
                <td>6 months</td>
                <td>Marketing</td>
              </tr>
              <tr>
                <td>YSC</td>
                <td>youtube.com</td>
                <td>Youtube, Google</td>
                <td>
                  Collects information about users and their activity on the website via embedded video players with the
                  aim of delivering targeted advertising.
                </td>
                <td>Session</td>
                <td>Marketing</td>
              </tr>
            </tbody>
          </table>
        </div>

        <Headline>Tredjepartscookies og overførsler</Headline>
        <p>
          Visse cookies sættes af tredjepartstjenester, der er indlejret på vores side (f.eks. YouTube, LinkedIn,
          Google). Disse tredjeparter kan behandle dine data uden for EØS (Det Europæiske Økonomiske Samarbejdsområde).
          I sådanne tilfælde sikrer vi, at der er truffet passende sikkerhedsforanstaltninger (såsom
          Standardkontraktbestemmelser/SCC).
        </p>
        <p>
          Vi er ikke ansvarlige for cookiepolitikkerne på tredjepartswebsteder, som du måtte besøge via links på vores
          side.
        </p>

        <Headline>Opdateringer til denne politik</Headline>
        <p>
          Vi kan opdatere denne cookiepolitik for at afspejle ændringer i de anvendte cookies eller lovmæssige krav.
          Besøg venligst denne side regelmæssigt for at holde dig informeret.
        </p>

        <Headline>Kontakt os</Headline>
        <p>
          Hvis du har spørgsmål om vores brug af cookies, bedes du kontakte os på:{' '}
          <a href="mailto:dpo@shine.fr">dpo@shine.fr</a>
        </p>
      </div>
    </div>
  )
}
